import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notification } from 'components/ds';
import { useSearchUser } from './useSearchUser';
import { useOtp } from './useOtp';
import { useOtpContext } from '../context/OtpContextProvider';
import { useSchema } from './useSchema';
import { useDynamicFieldsStorage } from './useDynamicFieldsStorage';
import { OtpFlow, SendOtpRequest } from '../_types/otp.types';
import { pageConfigs } from '../configs/pageConfigs';
import { useRegisterUserLogic } from './useRegisterUserWorkflow';
import { otpRequestChannelTypeMapper } from '../../../utils/otpRequestChannelTypeMapper';
import { RouteEnum } from '../constantes/RouteEnum';

interface OtpScenario {
  participantId: string;
  flow: OtpFlow;
  needsRegistration: boolean;
}

export const useOtpLogic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {userData, loading: userLoading} = useSearchUser();
  const {sendOtpRequest, loading: otpLoading} = useOtp();
  const {selectedChannel, setCurrentFlow} = useOtpContext();
  const {checkIsOpenFlow} = useSchema();
  const {registrationData} = useDynamicFieldsStorage();
  const {handleRegisterUser, isRegistering} = useRegisterUserLogic();
  
  const [displayUserData, setDisplayUserData] = useState<any>(null);
  
  const isOpenFlow = useMemo(() => checkIsOpenFlow(), [checkIsOpenFlow]);
  
  /**
   * Detecta se o fluxo atual é de reset de senha baseado na URL
   */
  const isResetPasswordFlow = useMemo(() => {
    const pathname = location.pathname;
    
    return pathname.includes('forgot-password') ||
      pathname.includes('reset-password') ||
      pathname.includes('password-reset');
  }, [location.pathname]);
  
  useEffect(() => {
    
    if (isOpenFlow) {
      // Para fluxo aberto, usa dados do registro se usuário não foi encontrado
      if (!userData?.userFound) {
        const contactInfo = {
          email: registrationData?.contactInfo?.email || '',
          phoneNumber: registrationData?.contactInfo?.phoneNumber || ''
        };
        
        if (contactInfo.email || contactInfo.phoneNumber) {
          setDisplayUserData({
            phoneNumber: contactInfo.phoneNumber,
            email: contactInfo.email
          });
        }
      } else {
        // Usuário encontrado em fluxo aberto, usar dados do usuário
        
        setDisplayUserData({
          phoneNumber: userData.phoneObfuscated || '',
          email: userData.emailObfuscated || '',
          participantId: userData.participantId
        });
      }
    } else if (userData?.userFound) {
      // Para fluxo fechado, sempre usar dados do usuário encontrado
      
      setDisplayUserData({
        phoneNumber: userData.phoneObfuscated || '',
        email: userData.emailObfuscated || '',
        participantId: userData.participantId
      });
    }
  }, [userData, registrationData, isOpenFlow, isResetPasswordFlow]);
  
  /**
   * Determina o cenário de OTP baseado nas condições do schema e usuário
   * Incluindo o fluxo de ResetPassword
   */
  const determineOtpScenario = useCallback((): OtpScenario => {
    const userExists = !!userData?.userFound;
    const isFirstAccess = userData?.firstAccess;
    const isOnboardingFinished = userData?.onboardingFinished;
    
    // CASO ESPECIAL: Fluxo de Reset Password
    if (isResetPasswordFlow && userExists && userData?.participantId) {
      
      return {
        participantId: userData.participantId,
        flow: 'ResetPassword',
        needsRegistration: false
      };
    }
    
    // CASO 1: Usuário não encontrado - sempre onboarding e precisa registrar
    if (!userExists) {
      return {
        participantId: '',
        flow: 'Onboarding',
        needsRegistration: true
      };
    }
    
    // CASO 2: Fluxo Open - usuário encontrado
    if (isOpenFlow && userExists && userData?.participantId) {
      // No fluxo aberto, se onboarding não finalizado = Onboarding
      // Se onboarding finalizado = Authentication
      const flow = !isOnboardingFinished ? 'Onboarding' : 'Authentication';
      
      return {
        participantId: userData.participantId,
        flow,
        needsRegistration: false
      };
    }
    
    // CASO 3: Fluxo Close - primeiro acesso e onboarding não finalizado
    if (!isOpenFlow && userExists && isFirstAccess && !isOnboardingFinished && userData?.participantId) {
      return {
        participantId: userData.participantId,
        flow: 'Onboarding',
        needsRegistration: false
      };
    }
    
    // CASO 4: Fluxo Close - não é primeiro acesso mas onboarding não finalizado
    if (!isOpenFlow && userExists && !isFirstAccess && !isOnboardingFinished && userData?.participantId) {
      return {
        participantId: userData.participantId,
        flow: 'Onboarding',
        needsRegistration: false
      };
    }
    
    // CASO 5: Fluxo Close - onboarding finalizado (independente de primeiro acesso)
    if (!isOpenFlow && userExists && isOnboardingFinished && userData?.participantId) {
      return {
        participantId: userData.participantId,
        flow: 'Authentication',
        needsRegistration: false
      };
    }
    
    throw new Error('Cenário de OTP inválido - verifique as condições do usuário e schema');
  }, [isOpenFlow, userData, isResetPasswordFlow]);
  
  /**
   * Método específico para lidar com Reset Password
   */
  const handleResetPasswordOtpRequest = useCallback(async (): Promise<void> => {
    if (!userData?.userFound || !userData?.participantId) {
      notification.error({
        message: 'Usuário não encontrado',
        description: 'Por favor, verifique se o CPF está correto e tente novamente.'
      });
      
      return;
    }
    
    try {
      // Definir o flow como ResetPassword
      setCurrentFlow('ResetPassword');
      
      const request: SendOtpRequest = {
        participantId: userData.participantId,
        flow: 'ResetPassword',
        channel: selectedChannel
      };
      
      const success = await sendOtpRequest(request);
      
      if (success) {
        navigate(RouteEnum.VALIDATE_OTP_RESET_PASSWORD);
      }
    } catch (error) {
      console.error('Erro ao processar Reset Password OTP:', error);
      notification.error({
        message: 'Erro ao enviar código',
        description: 'Não foi possível enviar o código de verificação. Tente novamente.'
      });
    }
  }, [userData, selectedChannel, setCurrentFlow, sendOtpRequest, navigate]);
  
  /**
   * Processa a solicitação de OTP seguindo a lógica dos cenários
   */
  const handleOtpRequest = useCallback(async (): Promise<void> => {
    // Se for fluxo de reset password, usar método específico
    if (isResetPasswordFlow) {
      return handleResetPasswordOtpRequest();
    }
    
    try {
      const scenario = determineOtpScenario();
      let participantIdToUse = scenario.participantId;
      
      if (scenario.needsRegistration) {
        
        try {
          participantIdToUse = await handleRegisterUser();
        } catch (registerError) {
          console.error('Erro específico no registro:', registerError);
          
          return;
        }
      }
      
      if (!participantIdToUse) {
        console.error('ParticipantId não disponível após determinação do cenário');
        notification.error({
          message: 'Erro interno',
          description: 'Não foi possível obter o identificador do usuário.'
        });
        
        return;
      }
      
      // Definir o flow no contexto
      setCurrentFlow(scenario.flow);
      
      const request: SendOtpRequest = {
        participantId: participantIdToUse,
        flow: scenario.flow,
        channel: selectedChannel
      };
      
      try {
        const success = await sendOtpRequest(request);
        
        if (success) {
          navigate(pageConfigs.otpRequest.navigateTo());
        }
      } catch (otpError) {
        console.error('Erro ao enviar OTP:', otpError);
        notification.error({
          message: 'Erro ao enviar código',
          description: 'Não foi possível enviar o código de verificação. Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro geral ao processar solicitação de OTP:', error);
      notification.error({
        message: 'Erro interno',
        description: 'Ocorreu um erro inesperado. Tente novamente.'
      });
    }
  }, [
    isResetPasswordFlow,
    handleResetPasswordOtpRequest,
    determineOtpScenario,
    handleRegisterUser,
    setCurrentFlow,
    selectedChannel,
    sendOtpRequest,
    navigate,
    isOpenFlow
  ]);
  
  /**
   * Determina a pergunta do código de segurança baseado no número de opções
   */
  const determineSecurityCodeQuestion = useCallback((optionsCount: number): string =>
    otpRequestChannelTypeMapper.getSecurityCodeQuestion(optionsCount), []);
  
  const isLoading = userLoading || otpLoading || isRegistering;
  
  const scenario = useMemo(() => {
    try {
      return determineOtpScenario();
    } catch (error) {
      console.error('Erro ao determinar cenário OTP:', error);
      
      return null;
    }
  }, [determineOtpScenario]);
  
  return {
    // Estados
    displayUserData,
    isLoading,
    isResetPasswordFlow,
    
    // Métodos
    handleOtpRequest,
    handleResetPasswordOtpRequest,
    determineOtpScenario,
    determineSecurityCodeQuestion,
    
    // Dados computados
    scenario
  };
};
